import { toReduxError, type ReduxError, GENERIC_FETCH_ERROR_MESSAGE } from '@shared/errors';
import { produce } from 'immer';
import { ofType } from 'redux-observable';
import { Observable, OperatorFunction, of, from } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Action, ActionType, createAsyncAction, createReducer } from 'typesafe-actions';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { CenterProducts } from '../../../../types/centerArticleType';
import { TypedEpic } from '../types';
import { CenterProductArticleType } from '../../../../types/centerProductArticleType';

export enum Actions {
  GET_CENTER_PRODUCTS_WITH_ID = 'naf/centers/GET_CENTER_PRODUCTS_WITH_ID',
  GET_CENTER_PRODUCTS_WITH_ID_SUCCESS = 'naf/centers/GET_CENTER_PRODUCTS_WITH_ID_SUCCESS',
  GET_CENTER_PRODUCTS_WITH_ID_FAIL = 'naf/centers/GET_CENTER_PRODUCTS_WITH_ID_FAIL',
  GET_CENTER_PRODUCTS_WITH_ID_CANCEL = 'naf/centers/GET_CENTER_PRODUCTS_WITH_ID_CANCEL',
}

export interface State {
  products: CenterProducts[] | null;
  isUpdating: boolean;
  errorState?: ReduxError;
}

export const initialState: State = {
  products: null,
  isUpdating: false,
};

export const actions = {
  getCenterProducts: createAsyncAction(
    Actions.GET_CENTER_PRODUCTS_WITH_ID, // request payload creator
    Actions.GET_CENTER_PRODUCTS_WITH_ID_SUCCESS, // success payload creator
    Actions.GET_CENTER_PRODUCTS_WITH_ID_FAIL, // failure payload creator
    Actions.GET_CENTER_PRODUCTS_WITH_ID_CANCEL, // optional cancel payload creator
  )<[string, { id?: number }], CenterProducts[], ReduxError, undefined>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getCenterProducts.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = true;
    }),
  )
  .handleAction(actions.getCenterProducts.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.products = action.payload;
    }),
  )
  .handleAction(actions.getCenterProducts.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.errorState = action.payload;
    }),
  )
  .handleAction(actions.getCenterProducts.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
    }),
  );

const fetchCenterProductsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_CENTER_PRODUCTS_WITH_ID),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getCenterProducts.request>[]
    >,
    switchMap(([action]: { payload: string; meta?: { id?: number } }[]) => {
      const url = ApiPaths[ApiType.apimNafNoApi].documents.get(action.payload);
      const params = action.meta?.id ? { centerId: action.meta.id } : undefined;

      return from(
        apiClient.get<CenterProductArticleType>(ApiType.apimNafNoApi, {
          url,
          params,
        }),
      ).pipe(
        map((response) => actions.getCenterProducts.success(response.data.products)),
        catchError((err) => of(actions.getCenterProducts.failure(toReduxError(err, GENERIC_FETCH_ERROR_MESSAGE)))),
      );
    }),
  );

export const epics: TypedEpic[] = [fetchCenterProductsEpic];
